// StorePhoneComponent.tsx
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "../useTranslation";

type Language = "ja" | "en";

const translations = {
  ja: {
    phoneNumber: "電話番号: ",
    phoneNumberPlaceholder: "電話番号を追加",
    setPhoneNumber: "設定",
    poweredBy: "Powered by IIC",
    iicHome: "IIC Home",
    termsOfUse: "利用規約",
    privacyPolicy: "プライバシーポリシー",
    tokenExpired: "トークンの有効期限が切れました。再度ログインしてください。",
    internalError: "内部エラーが発生しました。",
    iicWebURL: "https://www.iic-hq.co.jp/",
    sitepolicyWebURL: "https://www.ihi.co.jp/sitepolicy/",
    privacyWebURL: "https://www.ihi.co.jp/privacy/",
  },
  en: {
    phoneNumber: "Phone Number: ",
    phoneNumberPlaceholder: "Add Phone Number",
    setPhoneNumber: "Set Phone Number",
    poweredBy: "Powered by IIC",
    iicHome: "IIC Home",
    termsOfUse: "Terms of Use",
    privacyPolicy: "Privacy Policy",
    tokenExpired: "Token has expired. Please re-login.",
    internalError: "An internal error has occurred.",
    iicWebURL: "https://www.iic-hq.co.jp/en/",
    sitepolicyWebURL: "https://www.ihi.co.jp/en/sitepolicy/",
    privacyWebURL: "https://www.ihi.co.jp/en/privacy/",
  },
};

function getLanguageFromURL(): Language {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get("lang");
  return lang === "en" ? "en" : "ja";
}

// Translate server/client error messages based on lang
const translateError = (lang: "ja" | "en", msg: string) => {
  if (lang === "en") {
    const map: { [k: string]: string } = {
      "トークンの有効期限が切れました。再度ログインしてください。":
        "Token has expired. Please re-login.",
      "内部エラーが発生しました。": "An internal error has occurred.",
      "データの取得中にエラーが発生しました。":
        "An error occurred while retrieving the data.",
      "認証されていません。": "Unauthorized.",
      "電話番号が無効です。再度確認してください。":
        "Phone number is invalid. Please check again.",
      "電話番号は有効な国からである必要があります。":
        "Phone number must be from a valid country.",
      "電話番号の更新中にエラーが発生しました。":
        "An error occurred while updating the phone number.",
    };
    if (map[msg]) return map[msg];
  }
  return msg;
};

function StorePhoneComponent({ setStep, setPhoneNumber }: any) {
  const phoneInputRef = useRef(null);
  const [error, setError] = useState<string | null>(null); // Add this state variable to hold an error message
  const intlTelInputRef = useRef<any>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isClicked, setIsClicked] = useState(false);
  const [language, setLanguage] = useState<Language>(getLanguageFromURL());

  const apiUrl =
    process.env.REACT_APP_EXPRESS_API_URL || "http://localhost:3001";

  // Check language parameter on mount and when URL changes
  useEffect(() => {
    const checkLanguage = () => {
      setLanguage(getLanguageFromURL());
    };
    // Check immediately on mount/reload
    checkLanguage();
    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", checkLanguage);
    return () => {
      window.removeEventListener("popstate", checkLanguage);
    };
  }, []);

  const t = translations[language];

  useEffect(() => {
    const handleKeyDown = (e: { key: string }) => {
      if (e.key === "Enter" && buttonRef.current) {
        // Check if the button is visible
        if (window.getComputedStyle(buttonRef.current).display !== "none") {
          buttonRef.current.click();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup listener when component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const requestVerificationCode = () => {
    if (isClicked) return; // Prevent multiple clicks. TODO: reset after X seconds
    setIsClicked(true); // Disable the button

    let body = {};
    if (intlTelInputRef.current) {
      const fullPhoneNumber = intlTelInputRef.current.getNumber();
      setPhoneNumber(fullPhoneNumber); // This number will include the country code
      body = { phone: fullPhoneNumber };
    }
    axios
      .post(`${apiUrl}/verify`, body, {
        headers: {
          authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      })
      .then((res) => {
        if (res.status === 401) {
          throw new Error(t.tokenExpired);
        } else if (res.status === 500) {
          throw new Error(t.internalError);
        }
        setStep("enter-verification-code");
      })
      .catch((error) => {
        let msg = error.response.data.status || error.message;
        setError(translateError(language, msg));
        setIsClicked(false);
      });
  };

  useEffect(() => {
    if (window.intlTelInput && phoneInputRef.current) {
      intlTelInputRef.current = window.intlTelInput(phoneInputRef.current, {
        utilsScript:
          "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
        onlyCountries: ["us", "ca", "mx", "jp", "ua", "cz"],
        nationalMode: false,
      });
    }
  }, []);

  return (
    <div className="center">
      <img className="logo" src="images/Dap3_logo.png" alt="IIC's Logo"></img>

      <form onSubmit={(e) => e.preventDefault()}>
        <table align="center" style={{ width: "80%" }}>
          <tbody>
            <tr>
              <td className="dlabel" style={{fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif'}}>{t.phoneNumber}</td>
              <td>
                <input
                  id="phone"
                  ref={phoneInputRef}
                  type="tel"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t.phoneNumberPlaceholder}
                  className="input"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </form>
      <br />
      <div
        className="dlabel"
        id="login_feedback"
        style={{ color: "#ff0000", display: "block", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}
      >
        &nbsp; {error} {/* Display the error message if there is one */}
      </div>
      <br />
      <button
        ref={buttonRef}
        onClick={requestVerificationCode}
        className="login_button"
        value={t.setPhoneNumber}
        disabled={isClicked}
        style={{ backgroundColor: isClicked ? "gray" : "", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}
      >
        {t.setPhoneNumber}
      </button>
      <div
        className="dlabel"
        style={{ margin: "0 auto", display: "block", paddingTop: "10%" }}
      >
        {" "}
        {t.poweredBy}
      </div>
      <div
        className="dlabel"
        style={{
          fontWeight: "normal",
          padding: "3%",
          paddingTop: "2%",
          margin: "0 auto",
          display: "block",
        }}
      >
        <a href={t.iicWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.iicHome}
        </a>{" "}
        |{" "}
        <a href={t.sitepolicyWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.termsOfUse}
        </a>{" "}
        |{" "}
        <a href={t.privacyWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.privacyPolicy}
        </a>
      </div>
    </div>
  );
}

export default StorePhoneComponent;
